import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Tabs, type TabsItem } from '@/shared/ui/navigation/Tabs';
import { usePerm } from '@/shared/hooks/usePerm';
import { WarehouseOwnedTab } from './OwnedTab';
import { WarehouseOrphanTab } from './OrphanTab';
import { WarehouseArrivedPackagesTab } from './ArrivedPackagesTab';
import { WarehouseArrivedParcelsTab } from './ArrivedParcelsTab';

type TabKey = 'owned' | 'orphan' | 'arrived-packages' | 'arrived-parcels';

/**
 * Omborxona page. Two tabs above the filter row:
 *   - "owned"  (default, /warehouse) — parcels with an owner in active workflow states.
 *   - "orphan" (/warehouse/orphan) — the legacy Egasiz posilkalar list (status=UNCLAIMED only).
 *
 * Each tab manages its own filter + pagination state, so switching tabs
 * doesn't bleed unrelated query params across.
 */
export const WidgetsWarehouseMain = () => {
  const { t } = useTranslation();

  // Tab selection lives in the URL path so refresh + back/forward keep the
  // user where they were. The route layer registers both /warehouse and
  // /warehouse/orphan, so direct visits to either work.
  const location = useLocation();
  const navigate = useNavigate();
  // Both tabs now live under explicit subpaths. The /warehouse root is a
  // permission-aware redirect (see WarehouseRedirect), so any pathname that
  // reaches this widget must end in either /owned or /orphan.
  const activeTab: TabKey = location.pathname.endsWith('/orphan')
    ? 'orphan'
    : location.pathname.endsWith('/arrived-packages')
      ? 'arrived-packages'
      : location.pathname.endsWith('/arrived-parcels')
        ? 'arrived-parcels'
        : 'owned';
  const setActiveTab = (next: TabKey): void => {
    const path =
      next === 'orphan'
        ? '/warehouse/orphan'
        : next === 'arrived-packages'
          ? '/warehouse/arrived-packages'
          : next === 'arrived-parcels'
            ? '/warehouse/arrived-parcels'
            : '/warehouse/owned';
    navigate(path, { replace: true });
  };
  const [scannerOpen, setScannerOpen] = useState(false);
  const canOrphan = usePerm('warehouse.view');
  const canOwned = usePerm('parcels.list');
  const canArrivedPackages = usePerm('warehouse.arrived_packages.view');
  const canArrivedParcels = usePerm('warehouse.arrived_parcels.view');

  // Tab order: Egasiz birinchida (owner spec 2026-06-01) — bu default tab ham.
  const tabs = useMemo<readonly TabsItem<TabKey>[]>(
    () => [
      ...(canOrphan ? [{ value: 'orphan' as const, label: t('warehouse.tabs.orphan') }] : []),
      ...(canOwned ? [{ value: 'owned' as const, label: t('warehouse.tabs.owned') }] : []),
      ...(canArrivedPackages
        ? [{ value: 'arrived-packages' as const, label: t('warehouseArrivals.tabs.packages') }]
        : []),
      ...(canArrivedParcels
        ? [{ value: 'arrived-parcels' as const, label: t('warehouseArrivals.tabs.parcels') }]
        : []),
    ],
    [t, canOrphan, canOwned, canArrivedPackages, canArrivedParcels],
  );

  return (
    <>
      <PageHeader title={t('warehouse.title')} />
      <div className="space-y-4 px-4 py-4 lg:px-6 lg:py-6">
        <Tabs items={tabs} value={activeTab} onChange={setActiveTab} />

        {activeTab === 'owned' ? (
          <WarehouseOwnedTab />
        ) : activeTab === 'arrived-packages' ? (
          <WarehouseArrivedPackagesTab />
        ) : activeTab === 'arrived-parcels' ? (
          <WarehouseArrivedParcelsTab />
        ) : (
          <WarehouseOrphanTab
            scannerOpen={scannerOpen}
            onScannerOpen={() => setScannerOpen(true)}
            onScannerClose={() => setScannerOpen(false)}
          />
        )}
      </div>
    </>
  );
};
